/**
 * Zugriff auf die von scripts/optimize-assets.js erzeugten Medien.
 *
 * Im Code werden Fotos nur ueber ihren Basisnamen referenziert
 * ("praxisreinigung"), nicht ueber einen Dateipfad. Die Namen sind ueber
 * `ImageName` an das Manifest gebunden - ein Tippfehler oder ein geloeschtes
 * Foto bricht damit den Build und nicht erst die Seite im Browser.
 */

import manifest from '../data/imageManifest.json';

export type ImageName = keyof typeof manifest.images;
export type VideoName = keyof typeof manifest.videos;

type ImageEntry = { widths: number[]; hash: string };
type VideoEntry = { hash: string };

const images: Record<ImageName, ImageEntry> = manifest.images;
const videos: Record<VideoName, VideoEntry> = manifest.videos;

const PUBLIC = process.env.PUBLIC_URL || '';

export type ResponsiveImage = {
  src: string;
  srcSet: string;
};

/**
 * Liefert src und srcSet fuer ein Foto. `src` zeigt auf die groesste
 * vorhandene Variante als Fallback fuer Browser ohne srcSet-Auswertung.
 */
export function responsiveImage(name: ImageName): ResponsiveImage {
  const { widths, hash } = images[name];
  const url = (w: number) => `${PUBLIC}/Images/${name}-${w}.${hash}.webp`;
  return {
    src: url(widths[widths.length - 1]),
    srcSet: widths.map((w) => `${url(w)} ${w}w`).join(', '),
  };
}

/**
 * Fertige Attribute fuer ein `<img>`. Buendelt src/srcSet/sizes mit den
 * Ladehinweisen, damit die Aufrufer nicht jedes Mal `loading` und `decoding`
 * einzeln setzen muessen (und es dabei unterschiedlich machen).
 */
export function imageProps(name: ImageName, sizes: string) {
  return {
    ...responsiveImage(name),
    sizes,
    loading: 'lazy' as const,
    decoding: 'async' as const,
  };
}

/**
 * Pfad des Social-Preview-Bildes (JPEG, 1200 px breit) fuer og:image.
 * Bewusst kein WebP: Facebook, LinkedIn und WhatsApp werten es dort bis
 * heute nicht zuverlaessig aus. Seo.tsx macht daraus die absolute URL.
 */
export function socialImagePath(name: ImageName): string {
  return `${PUBLIC}/Images/${name}-og.${images[name].hash}.jpg`;
}

/** Video plus zugehoeriges Poster-Standbild. */
export function videoAsset(name: VideoName): { src: string; poster: string } {
  const { hash } = videos[name];
  return {
    src: `${PUBLIC}/Videos/${name}.${hash}.mp4`,
    poster: `${PUBLIC}/Videos/${name}-poster.${hash}.webp`,
  };
}

/**
 * `sizes`-Werte der Stellen, an denen Fotos vorkommen. Die Werte sind an den
 * gerenderten Bildbreiten gemessen, nicht geschaetzt - stimmen sie nicht mit
 * dem CSS ueberein, laedt der Browser zu grosse oder zu unscharfe Varianten.
 * Wer ein Grid oder einen Breakpoint aendert, muss den passenden Wert hier
 * nachziehen.
 */
export const SIZES = {
  /** Karten im Leistungs-Raster (Startseite, /leistungen). Umbrueche bei 600 und 900 px. */
  card:
    '(max-width: 599px) calc(100vw - 64px), ' +
    '(max-width: 899px) calc((100vw - 92px) / 2), ' +
    '(max-width: 1163px) calc((100vw - 116px) / 3), ' +
    '349px',
  /** Spotlight-Foto der Praxisreinigung auf der Startseite. Umbruch bei 900 px. */
  spotlight:
    '(max-width: 899px) calc(100vw - 100px), ' +
    '(max-width: 1163px) calc((100vw - 100px) * 0.47), ' +
    '499px',
  /** Showcase-Foto auf den Detailseiten. Umbruch bei 800 px, Deckel bei 1000 px Grid-Breite. */
  showcase:
    '(max-width: 799px) calc(100vw - 64px), ' +
    '(max-width: 1063px) calc((100vw - 95px) * 0.475), ' +
    '461px',
  /** Vollflaechiges Hero-Hintergrundbild der Detailseiten. */
  hero: '100vw',
  /**
   * Vorher/Nachher: eine Karte pro Zeile, die beiden Fotos nebeneinander erst
   * ab 560 px. Darunter stehen sie untereinander und nutzen die volle Breite.
   */
  comparison:
    '(max-width: 559px) calc(100vw - 66px), ' +
    '(max-width: 1023px) calc((100vw - 66px) / 2), ' +
    '479px',
} as const;
